import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import PageMeta from '../../modules/PageMeta';
import PageSection from '../../modules/PageSection';
import SidebarColumn from '../../modules/SidebarColumn';
import ContentColumn from '../../modules/ContentColumn';
import PageSidebar from '../../modules/PageSidebar';
import Page from '../../pages/Page';
import SearchUsers from '../../forms/SearchUsers';
import AlertBox from '../../modules/AlertBox';
import AlertMessage from '../../modules/AlertMessage';
import LoadMoreButton from '../../modules/LoadMoreButton';
import Overlay from '../../modules/Overlay';
import InlineButtonPanel from '../../modules/InlineButtonPanel';
import Button from '../../modules/Button';

import debounce from '../../../services/utils/debounce';

import * as adminActions from '../../../actions/admin';

import styles from './styles.scss';
import PageHeader from "../../modules/PageHeader/index";

class AdminSearchUsers extends React.Component {

    constructor() {
        super();

        this.searchUsers = debounce(this._searchUsers.bind(this), 200);
    }

    componentDidMount() {

        this.props.resetUserList();

        const { searchText } = this.props;

        if (searchText) {
            this.props.searchUsers(searchText);
        }
    }

    _searchUsers(values) {

        return this.props.searchUsers(values.searchText || '');
    }

    render() {

        const {
            users,
            usersInitialised,
            isMoreUsersToLoad,
            isSearchingUsers,
            searchText,
            userToDelete,
            userToAuthenticateAs
        } = this.props;

        return (
            <Page>
                <PageMeta title="Search Users" />

                <SidebarColumn>
                    <PageSidebar />
                </SidebarColumn>

                <ContentColumn>

                    <PageHeader>
                        <h1>User Management</h1>
                    </PageHeader>

                    <PageSection>
                        <SearchUsers initialValues={{ searchText }} onChange={this.searchUsers.bind(this)} />
                    </PageSection>

                    <AlertMessage>
                        {usersInitialised && users.length === 0 &&
                            <PageSection>
                                <AlertBox
                                    theme="warning"
                                    standalone="true"
                                    tabIndex={0}
                                >
                                    We couldn't find any users in this search.
                                </AlertBox>
                            </PageSection>
                        }
                    </AlertMessage>

                    {users.length > 0 &&
                        <PageSection>
                            {users.map(user => {
                                return (
                                    <div key={user.id} className={styles['user']}>
                                        <span className={styles['user__name']}>{ `Name: ` + user.full_name }</span>
                                        <span className={styles['user__email']}>{ `Email: ` + user.email }</span>

                                        <div className={styles['action-panel']}>
                                            <button className={styles['user-action']} onClick={this.props.showAuthenticateConfirmation.bind(this, user)}>
                                                Authenticate
                                            </button>
                                            <Link className={styles['user-action']} to={`/admin/audit-logs/${user.id}`}>
                                                Audit
                                            </Link>
                                            <Link className={styles['user-action']} to={`/admin/user-licences/${user.id}`}>
                                                Licences
                                            </Link>
                                            <Link className={styles['user-action']} to={`/admin/edit-user/${user.id}`}>
                                                Edit
                                            </Link>
                                            <button className={styles['user-action--danger']} onClick={this.props.showDeleteConfirmation.bind(this, user)}>
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                )
                            })}

                            {!isSearchingUsers && isMoreUsersToLoad &&
                                <LoadMoreButton
                                    onClick={this.props.loadMoreUsers.bind(this, searchText)}
                                >
                                    Load More
                                </LoadMoreButton>
                            }
                        </PageSection>
                    }

                    {userToDelete &&
                        <Overlay onRequestClose={this.props.hideDeleteConfirmation} isOpen={true}>
                            <p>
                                Are you sure you want to delete <strong>{ userToDelete.full_name }</strong>?
                            </p>

                            <p>
                                They will also be removed from any RHE services they have been using.
                            </p>

                            <InlineButtonPanel>
                                <Button disabled={this.props.isRemovingUser} onClick={this.props.deleteUser.bind(this, userToDelete)}>Yes</Button>
                                <Button onClick={this.props.hideDeleteConfirmation}>No</Button>
                            </InlineButtonPanel>
                        </Overlay>
                    }

                    {userToAuthenticateAs &&
                        <Overlay onRequestClose={this.props.hideAuthenticateConfirmation} isOpen={true}>
                            <p>
                                Are you sure you want to authenticate as <strong>{ userToAuthenticateAs.full_name }</strong>?
                            </p>

                            <p>
                                You will be authenticated as this user in all RHE services. Please be careful.
                            </p>

                            <InlineButtonPanel>
                                <Button disabled={this.props.isAuthenticatingAsUser} onClick={this.props.authenticateAsUser.bind(this, userToAuthenticateAs)}>Yes</Button>
                                <Button onClick={this.props.hideAuthenticateConfirmation}>No</Button>
                            </InlineButtonPanel>
                        </Overlay>
                    }

                </ContentColumn>
            </Page>
        )
    }
}

const mapStateToProps = state => {
    return {
        isSearchingUsers: state.admin.isSearchingUsers,
        isMoreUsersToLoad: state.admin.isMoreUsersToLoad,
        users: state.admin.searchUsers,
        usersInitialised: state.admin.searchUsersInitialised,
        searchText: state.admin.searchText,
        isRemovingUser: state.admin.isRemovingUser,
        isAuthenticatingAsUser: state.admin.isAuthenticatingAsUser,
        userToDelete: state.admin.userToDelete,
        userToAuthenticateAs: state.admin.userToAuthenticateAs
    }
};

const mapDispatchToProps = (dispatch) => {
    return {
        resetUserList() {
            dispatch(adminActions.resetUserList());
        },
        searchUsers(searchText) {
            dispatch(adminActions.searchUsers(searchText));
        },
        loadMoreUsers(searchText) {
            dispatch(adminActions.loadMoreUsers(searchText));
        },
        deleteUser(user) {
            dispatch(adminActions.deleteUser(user));
        },
        authenticateAsUser(user) {
            dispatch(adminActions.authenticateAsUser(user));
        },
        showDeleteConfirmation(user) {
            dispatch(adminActions.changeUserToDelete(user));
        },
        showAuthenticateConfirmation(user) {
            dispatch(adminActions.changeUserToAuthenticate(user));
        },
        hideDeleteConfirmation() {
            dispatch(adminActions.changeUserToDelete(null));
        },
        hideAuthenticateConfirmation() {
            dispatch(adminActions.changeUserToAuthenticate(null));
        }
    }
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminSearchUsers);